import type { ProductContext } from "../types";

interface JsonLdOffer {
  url?: string;
  [key: string]: unknown;
}

interface JsonLdNode {
  "@type"?: string | string[];
  "@graph"?: JsonLdNode[];
  "@id"?: string;
  productID?: string;
  sku?: string;
  mpn?: string;
  gtin?: string;
  gtin8?: string;
  gtin12?: string;
  gtin13?: string;
  gtin14?: string;
  url?: string;
  offers?: JsonLdOffer | JsonLdOffer[];
  [key: string]: unknown;
}

function extractGtin(node: JsonLdNode): string | null {
  return node.gtin ?? node.gtin13 ?? node.gtin12 ?? node.gtin14 ?? node.gtin8 ?? null;
}

function normalizeDigits(value: string): string {
  return value.replace(/^0+(?=\d)/, "");
}

// Algumas lojas publicam mais de um JSON-LD @type:Product na mesma página (ex:
// um bloco combinado de Organization/BreadcrumbList/Product/Website e outro só
// de Product vindo de um plugin de Open Graph) e eles discordam entre si sobre
// o gtin — um traz o EAN real, o outro reaproveita o SkuId interno da VTEX pra
// preencher o campo. Detectamos isso comparando gtin e sku DENTRO do mesmo nó:
// se forem o mesmo número (ignorando zeros à esquerda), é sinal de que o gtin
// é sintético, não um EAN de verdade — pulamos pro próximo candidato.
function pickRealGtin(candidates: JsonLdNode[]): string | null {
  let fallback: string | null = null;

  for (const candidate of candidates) {
    const gtin = extractGtin(candidate);
    if (!gtin) continue;
    if (fallback === null) fallback = gtin;

    const looksSynthetic =
      candidate.sku != null && normalizeDigits(gtin) === normalizeDigits(candidate.sku);
    if (!looksSynthetic) return gtin;
  }

  return fallback;
}

// Algumas lojas Tray publicam a URL canônica só dentro de "offers.url" (o template
// de JSON-LD gerado pela própria Tray faz isso), nunca no nó Product em si — nem em
// "url" nem em "@id". "offers" também pode vir como array (AggregateOffer/Offer[]),
// então pegamos a primeira com "url" definida.
function extractOfferUrl(node: JsonLdNode): string | undefined {
  const offers = node.offers;
  if (!offers) return undefined;

  const offerList = Array.isArray(offers) ? offers : [offers];
  for (const offer of offerList) {
    if (typeof offer?.url === "string") return offer.url;
  }

  return undefined;
}

function firstDefined<T>(
  candidates: JsonLdNode[],
  pick: (node: JsonLdNode) => T | undefined,
): T | undefined {
  for (const candidate of candidates) {
    const value = pick(candidate);
    if (value !== undefined) return value;
  }
  return undefined;
}

// O que precisamos é o RefId do produto (o código que o lojista cadastra no
// admin da VTEX e é usado pra casar com o catálogo da IndexaAI) — não o SkuId
// (chave numérica interna, autogerada pela VTEX). Alguns temas publicam um
// segundo JSON-LD @type:Product (via plugin de Open Graph) onde "sku"/"gtin"
// saem preenchidos com o SkuId em vez do RefId (ex: "00007032" nos dois campos,
// quando o RefId real é "682527") — mas essa mesma meta tag do Open Graph traz
// o RefId correto.
function extractRetailerItemId(): string | null {
  const meta = document.querySelector('meta[property="product:retailer_item_id"]');
  const content = meta?.getAttribute("content")?.trim();
  return content ? content : null;
}

// schema.org/Product não tem um campo "slug" — derivamos do "url" (sempre presente
// no JSON-LD da VTEX/Tray) pegando o último segmento do path, sem a query/hash.
// URLs de PDP da VTEX seguem o padrão "/<slug>/p" — o "/p" final é um marcador de
// rota (toda loja VTEX tem, independente do tema), não faz parte do slug.
function productPathSegments(url?: string): string[] {
  if (!url) return [];

  try {
    const segments = new URL(url, window.location.origin).pathname.split("/").filter(Boolean);
    if (segments[segments.length - 1] === "p") segments.pop();
    return segments;
  } catch {
    return [];
  }
}

export function extractSlugFromUrl(url?: string): string | null {
  const segments = productPathSegments(url);
  return segments[segments.length - 1] ?? null;
}

function safeDecode(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

// Caminho inteiro da PDP ("categoria/sub/produto"), decodificado. Ao contrário do slug,
// não perde segmentos do meio: nomes com "/" (ex: "1/8") viram pasta na URL da loja.
export function extractSlugPathFromUrl(url?: string): string | null {
  const segments = productPathSegments(url);
  if (segments.length === 0) return null;
  return segments.map(safeDecode).join("/");
}

// Não é um padrão de todas as lojas VTEX, mas quando o tema gera o slug da PDP
// terminando em "-<RefId>" (ex: "...-682527"), esse sufixo é um jeito confiável
// de conseguir o RefId sem depender de meta tag ou de campo do JSON-LD.
function extractSkuFromSlug(slug: string | null): string | null {
  if (!slug) return null;
  const match = slug.match(/-(\d{3,})$/);
  return match?.[1] ?? null;
}

function isProductNode(node: JsonLdNode): boolean {
  const type = node["@type"];
  if (!type) return false;
  return Array.isArray(type) ? type.includes("Product") : type === "Product";
}

const MAX_WALK_DEPTH = 6;

// Nem sempre o Product fica no topo do script ou dentro de "@graph" — alguns temas
// aninham vários nós (Organization, BreadcrumbList, Product, Website...) sob chaves
// nomeadas livres (ex: {"organization": {...}, "product": {"@type": "Product", ...}}).
// Por isso caminhamos recursivamente por todo o JSON de cada script, coletando TODO
// nó @type:Product que existir — inclusive quando a página publica mais de um.
function collectProductNodes(value: unknown, depth: number, acc: JsonLdNode[]): void {
  if (depth > MAX_WALK_DEPTH || !value || typeof value !== "object") return;

  if (Array.isArray(value)) {
    for (const item of value) collectProductNodes(item, depth + 1, acc);
    return;
  }

  const node = value as JsonLdNode;
  if (isProductNode(node)) acc.push(node);

  // "@graph" também precisa ser percorrido (é onde a maioria dos temas VTEX/Tray
  // aninha os nós) — só "@type"/"@context"/"@id" são sempre primitivos e caem fora
  // pelo check de typeof acima, então não precisamos filtrar chaves aqui.
  for (const key of Object.keys(node)) {
    collectProductNodes(node[key], depth + 1, acc);
  }
}

/**
 * VTEX instala por padrão o app vtex.structured-data, que sempre publica um
 * JSON-LD @type:Product na PDP. É um sinal mais confiável entre plataformas
 * do que qualquer seletor de DOM específico de tema (que varia por loja).
 */
export function findProductJsonLd(): ProductContext | null {
  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]'),
  );

  const candidates: JsonLdNode[] = [];
  for (const script of scripts) {
    try {
      const parsed = JSON.parse(script.textContent ?? "");
      collectProductNodes(parsed, 0, candidates);
    } catch {
      // ignora scripts com JSON inválido e continua procurando nos próximos
    }
  }

  const product = candidates[0];
  if (!product) return null;

  // schema.org/Product costuma expor a URL canônica em "url", mas alguns nós só
  // trazem "@id" (que na prática é a mesma URL da PDP nesses casos), e outros
  // (template padrão de JSON-LD da Tray) só trazem em "offers.url".
  const url = firstDefined(candidates, (node) => node.url ?? node["@id"] ?? extractOfferUrl(node));
  const slug = extractSlugFromUrl(url);
  const slugPath = extractSlugPathFromUrl(url);

  // Ordem de confiança pro RefId: meta tag Open Graph (product:retailer_item_id)
  // > "mpn" do JSON-LD > sufixo numérico do slug > por último "sku" cru do
  // JSON-LD, que às vezes é na verdade o SkuId interno da VTEX, não o RefId.
  const sku =
    extractRetailerItemId() ??
    firstDefined(candidates, (node) => node.mpn) ??
    extractSkuFromSlug(slug) ??
    product.sku ??
    null;

  return {
    productId: product.productID ?? null,
    sku: sku ?? null,
    gtin: pickRealGtin(candidates),
    slug,
    slugPath,
  };
}

export function hasProductJsonLd(): boolean {
  return findProductJsonLd() !== null;
}
